// CSV for import (plan stage 2.4): RFC 4180 parsing, values formatted the way people read them. The file itself is written by the server (@confdeck/core-server/src/export.ts).

import { recordTitle, taxonomyLocked, textKey, type FieldDef, type Id, type MetaIndex } from '@confdeck/core-shared';
import type { Store } from '../sync/store.ts';
import { normalizePhone, parseMoney } from '../fields/format.ts';

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  // Excel saves CSV with a BOM and often with semicolons in Russian locales.
  const src = text.replace(/^﻿/, '');
  const firstLine = src.slice(0, src.indexOf('\n') >>> 0);
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c !== '')) rows.push(row);
  return rows;
}

/** Header → field by name or key, ignoring case and «ё». */
export function guessMapping(headers: string[], fields: FieldDef[]): (string | null)[] {
  return headers.map((h) => fields.find((f) => textKey(f.name) === textKey(h) || f.key === h.trim())?.key ?? null);
}

export interface Prepared {
  /** Rows ready to import; `line` is the line of the file the row came from (the first line is the header). */
  rows: { id: string; data: Record<string, unknown>; line: number }[];
  errors: string[];
  newTerms: { taxonomyId: Id; name: string; id: Id }[];
  /** Linked records the import has to create: one per title in a target section. */
  newRecords: { typeId: Id; id: Id; title: string }[];
}

/**
 * Converts CSV cells to stored values: money in kopecks, phones to E.164, terms by name (new labels are
 * created), relations by the title of an existing record. A title nobody has yet becomes a new linked record
 * where the person may write; a row with errors is reported, not imported.
 */
export function prepare(
  rows: string[][],
  mapping: (string | null)[],
  typeId: Id,
  store: Store,
  newId: () => string,
  canCreate: (typeId: Id) => boolean,
): Prepared {
  const meta: MetaIndex = store.meta;
  const errors: string[] = [];
  const newTerms: Prepared['newTerms'] = [];
  const newRecords: Prepared['newRecords'] = [];
  const titleIndex = new Map<Id, Map<string, Id>>();
  const byTitle = (target: Id, title: string) => {
    let m = titleIndex.get(target);
    if (!m) {
      m = new Map();
      for (const id of store.recordIds(target)) m.set(textKey(recordTitle(store.record(id), meta)), id);
      titleIndex.set(target, m);
    }
    return m.get(textKey(title));
  };
  /**
   * A value of a reference list: an existing term, or a new label the list allows. A closed list (an app sets
   * it, or it is the final stages of a funnel) has no new values: a missing one is an error of the row
   * (platform П15).
   */
  const termByName = (taxonomyId: Id, name: string): { id: Id } | { error: string } => {
    const t = meta.terms(taxonomyId).find((x) => textKey(x.name) === textKey(name)) ?? newTerms.find((x) => x.taxonomyId === taxonomyId && textKey(x.name) === textKey(name));
    if (t) return { id: t.id };
    const tax = meta.taxonomy(taxonomyId);
    if (tax?.style !== 'labels') return { error: `нет значения «${name}»` };
    if (taxonomyLocked(meta, taxonomyId)) return { error: `нет такого значения «${name}»` };
    const created = { taxonomyId, name: name.trim(), id: newId() };
    newTerms.push(created);
    return { id: created.id };
  };
  const out: Prepared['rows'] = [];
  // A linked record is created only where the title is an ordinary field and the person may write there.
  const createdByTitle = new Map<string, Id>();
  const canCreateLinked = (target: Id) => {
    const t = meta.type(target);
    const title = t ? meta.field(target, t.titleField) : undefined;
    return !!title && !title.options.managed && canCreate(target);
  };
  /** One new record per title in a target section; the same title never gets two records. */
  const linkedId = (target: Id, title: string): Id => {
    const key = `${target}\n${textKey(title)}`;
    const already = createdByTitle.get(key);
    if (already) return already;
    const id = newId();
    createdByTitle.set(key, id);
    newRecords.push({ typeId: target, id, title });
    return id;
  };
  rows.forEach((cells, n) => {
    const data: Record<string, unknown> = {};
    const rowErr: string[] = [];
    /** Relations are resolved after the row is checked: a row that is not imported creates no records. */
    const relations: { field: FieldDef; targetTypeId: Id; many: boolean; ids: (Id | null)[]; missing: string[] }[] = [];
    mapping.forEach((key, c) => {
      if (!key) return;
      const f = meta.field(typeId, key);
      const raw = (cells[c] ?? '').trim();
      if (!f || !raw) return;
      switch (f.fieldType) {
        case 'number': { const v = Number(raw.replace(/\s/g, '').replace(',', '.')); if (Number.isNaN(v)) rowErr.push(`${f.name}: не число`); else data[key] = v; break; }
        case 'money': { const v = parseMoney(raw); if (v === null || Number.isNaN(v)) rowErr.push(`${f.name}: не сумма`); else data[key] = v; break; }
        case 'phone': data[key] = normalizePhone(raw); break;
        case 'url': data[key] = /^https?:\/\//.test(raw) ? raw : `https://${raw}`; break;
        case 'checkbox': data[key] = /^(да|yes|true|1|\+)$/i.test(raw); break;
        case 'date': { const m = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/); data[key] = m ? `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}` : raw.slice(0, 10); break; }
        case 'taxonomy': {
          const names = f.options.multiple ? raw.split(/[;,]/).map((x) => x.trim()).filter(Boolean) : [raw];
          const ids: (Id | null)[] = [];
          const missing: string[] = [];
          for (const nm of names) {
            const t = termByName(f.options.taxonomyId!, nm);
            if ('id' in t) ids.push(t.id);
            else { ids.push(null); missing.push(t.error); }
          }
          if (missing.length) rowErr.push(`${f.name}: ${missing.join(', ')}`);
          else data[key] = f.options.multiple ? ids : ids[0];
          break;
        }
        case 'relation': {
          const target = meta.relationTarget(f);
          if (!target) break;
          const titles = raw.split(';').map((x) => x.trim()).filter(Boolean);
          // A field that holds one record keeps the first title; the rest of the cell is ignored.
          const used = target.many ? titles : titles.slice(0, 1);
          const ids: (Id | null)[] = [];
          const missing: string[] = [];
          for (const t of used) {
            const found = byTitle(target.typeId, t);
            if (found) ids.push(found);
            else { ids.push(null); missing.push(t); }
          }
          relations.push({ field: f, targetTypeId: target.typeId, many: target.many, ids, missing });
          break;
        }
        case 'user': { const u = meta.users().find((x) => textKey(x.name) === textKey(raw) || x.email === raw.toLowerCase()); if (u) data[key] = u.id; else rowErr.push(`${f.name}: нет пользователя «${raw}»`); break; }
        // A query is a section and a filter, set in the card (decision 043): a file's text is not one.
        case 'query': rowErr.push(`${f.name}: запрос задаётся в карточке, не файлом`); break;
        default: data[key] = raw;
      }
    });
    for (const f of meta.fields(typeId)) if (f.required && f.fieldType !== 'relation' && data[f.key] == null) rowErr.push(`${f.name}: обязательное поле пусто`);
    // A title nobody has yet is a new linked record; where that is not allowed the row is reported.
    for (const r of relations) {
      if (!r.missing.length) continue;
      if (!canCreateLinked(r.targetTypeId)) { rowErr.push(`${r.field.name}: не найдено «${r.missing[0]}»`); break; }
    }
    if (rowErr.length) { errors.push(`Строка ${n + 2}: ${rowErr.join('; ')}`); return; }
    for (const r of relations) {
      let k = 0;
      const ids = r.ids.map((id) => id ?? linkedId(r.targetTypeId, r.missing[k++]!));
      data[r.field.key] = r.many ? ids : ids.slice(0, 1);
    }
    out.push({ id: newId(), data, line: n + 2 });
  });
  return { rows: out, errors, newTerms, newRecords };
}
